<template>
  <td 
    class="editableCell"
    :class="{ 'cellFocused': isFocused && !invalid && !isEditing, 
      'cellFocusedInvalid': isFocused && invalid && !isEditing, 
      'cellInvalid': invalid && !isFocused && !isEditing }"
    @click="setFocus"
  >
    <div class="cellWrapper">
      <!-- Editing mode -->
      <div
        v-if="isEditing"
        class="cellContent"
        :class="{ 'cellContentInvalid': invalid, 'cellEditing': !invalid }"
        contenteditable="true"
        ref="inputRef"
        @keydown="handleKeydown"
        @blur="finishEdit"
        @input="handleInput"
        @paste.prevent="handlePaste"
      >
        {{ displayValue }}
      </div>

      <!-- Display mode -->
      <span 
        v-else 
        class="cellDisplay"
        :class="{ 'nonEditable': !editable }"
        :data-placeholder="!displayValue ? placeholder : ''"
        @dblclick="requestEdit"
        @keydown="handleDisplayKeydown"
        tabindex="0"
        ref="displayRef"
      >
        {{ displayValue }}
      </span>
    </div>
  </td>
</template>

<script>
import { EventBus } from '@/event-bus.js';
import Utils from '@/common/utils.js';
import validationMixin from '@/common/validationMixin.js';
import validationRules from '@/common/validationRules';
import ShowMessage from '@/components/messages/ShowMessage.js';

export default {
  name: 'EditableDateCell',
  mixins: [validationMixin, ShowMessage],
  props: {
    name: { type: String, required: true },
    value: [String, Number],
    editable: { type: Boolean, default: true },
    isFocused: { type: Boolean, default: false },
    validatorRules: { type: Array, default: () => [] },
  },
  data() {
    const format = Utils.getShortDateFormat();
    return {
      isEditing: false,
      invalid: false,
      placeholder: format,
      rawDigits: '',
      displayValue: '',
      isDeleting: false
    };
  },
  computed: {
    row() {
      const split = this.name.split('-');
      return split.length === 3 ? parseInt(split[1]) : -1;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        const formatted = newValue ? Utils.formatterDateToString(newValue, 'YYYY-MM-DD') : '';
        this.displayValue = formatted;
        this.rawDigits = formatted.replace(/\D/g, '');
      }
    }
  },
  methods: {
    setFocus() {
      this.$emit('cellFocus', this.row, this.name);
    },
    requestEdit() {
      if (!this.editable) return;
      this.$emit('requestEdit', { row: this.row, name: this.name });
    },
    startEdit() {
      if (!this.editable) {
        this.$nextTick(() => this.$refs.displayRef.focus());
        return;
      }
      
      this.isEditing = true;
      this.$nextTick(() => {
        const el = this.$refs.inputRef;
        el.focus();

        // Seleccionar todo el contenido
        const range = document.createRange();
        range.selectNodeContents(el);

        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      });
    },
    finishEdit() {
      const format = Utils.getShortDateFormat();
      const valid = this.validateCell(this.displayValue);

      if (valid) {
        const iso = Utils.formatterToISO(this.displayValue, format);
        this.isEditing = false;
        const changed = iso !== Utils.formatterToISO(this.value);

        if (changed) {
          this.$emit('input', iso);
          this.emitChanged(iso);
        }
      } else {
        this.isEditing = true;
        //this.$emit('cellFocus', this.row, this.name);
        //this.$nextTick(() => this.$refs.inputRef.focus());
        this.showMessage(this.$tc('Msg.Error'), this.invalidMessage(this.name), "errorAutoHide");
      }
    },

    handleKeydown(event) { 
      // Detectar Ctrl+V o Cmd+V manualmente
      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        // Si el navegador no lanza el evento paste por sí solo, 
        // lo dejamos pasar para que lo intente o lo manejamos aquí.
        return; 
      }

      this.isDeleting = event.key === 'Backspace' || event.key === 'Delete';
      const caret = this.getCaretPosition(this.$refs.inputRef);

      // Si el usuario escribe "/" en la posición 2 o 5, solo movemos el cursor
      if (event.key === '/') {
        if (caret === 2 || caret === 5) {
          // No hacemos nada, el cursor ya está en la posición de la barra
          return;
        }
        event.preventDefault();
        return;
      }

      const allowedKeys = ['Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Tab','Enter'];
      if (allowedKeys.includes(event.key)) {
        switch (event.key) { 
          case 'Enter': 
            event.preventDefault(); 
            this.finishEdit(event); 

            if (!this.invalid) this.$emit('nextCell'); 
            break; 
          case 'Tab': 
            event.preventDefault(); 
            this.finishEdit(event); 

            if (!this.invalid) { 
              this.$emit(event.shiftKey ? 'prevCell' : 'nextCell'); 
            } 
            break; 
          case 'ArrowUp': 
            if (this.caretAtStart()) { 
              event.preventDefault(); 
              this.finishEdit(event); 

              if (!this.invalid) this.$emit('upCell'); 
            } 
            break; 
          case 'ArrowDown': 
            if (this.caretAtEnd()) { 
              event.preventDefault(); 
              this.finishEdit(event); 

              if (!this.invalid) this.$emit('downCell'); 
            } 
            break; 
          case 'ArrowLeft': 
            if (this.caretAtStart()) { 
              event.preventDefault(); 
              this.finishEdit(event); 

              if (!this.invalid) this.$emit('prevCell'); 
            } 
            break; 
          case 'ArrowRight': 
            if (this.caretAtEnd()) { 
              event.preventDefault(); 
              this.finishEdit(event); 

              if (!this.invalid) this.$emit('nextCell'); 
            } break; 
        } 

        return;
      }

      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    },

    handleDisplayKeydown(event) {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();

          if (this.editable) {
            this.requestEdit();
          } else {
            this.$emit('nextCell'); 
          }
          break;

        case 'F2': // opcional, estilo Excel
          if (this.editable) {
            event.preventDefault();
            this.requestEdit();
          }
          break;

        case 'Tab':
          event.preventDefault();
          this.$emit(event.shiftKey ? 'prevCell' : 'nextCell');
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.$emit('upCell');
          break;

        case 'ArrowDown':
          event.preventDefault();
          this.$emit('downCell');
          break;

        case 'ArrowLeft':
          event.preventDefault();
          this.$emit('prevCell');
          break;

        case 'ArrowRight':
          event.preventDefault();
          this.$emit('nextCell');
          break;
      }
    },

    handleInput(e) {
      console.log('handleInput')
      const el = e.target;
      const originalText = el.textContent;
      
      // 1. Obtenemos posición inicial del cursor
      const caretBefore = this.getCaretPosition(el);
      
      // 2. Extraemos solo los números
      let digits = originalText.replace(/\D/g, '').slice(0, 8);
      this.rawDigits = digits;

      // 3. Aplicamos la máscara (la lógica que ya tienes con keepTrailingSlash)
      const endsWithSlash = originalText.endsWith('/');
      const masked = this.applyMask(digits, endsWithSlash);

      // 4. Si el texto formateado es distinto al actual, actualizamos el DOM
      if (masked !== originalText) {
        el.textContent = masked;

        this.$nextTick(() => {
          // CALCULO DEL NUEVO CURSOR:
          // Si el usuario añadió un número y se insertó una barra automática, 
          // la longitud de la cadena creció más de lo que el usuario escribió.
          let newCaret = caretBefore;

          // Si no estamos borrando y se insertó una barra justo antes o en la posición del cursor
          if (!this.isDeleting) {
            // Caso crítico: Escribes el 3er o 5to dígito y aparece la barra
            // Si el carácter en la posición del cursor (o justo antes) es ahora una barra, saltamos.
            if (masked.length > originalText.length) {
              // Si el cursor estaba al final o sobre la posición de la nueva barra
              if (caretBefore === 3 || caretBefore === 6) {
                newCaret = caretBefore + 1;
              }
            }
          }

          this.setCaretPosition(el, Math.min(newCaret, masked.length));
        });
      }
      this.displayValue = masked;
    },

    handlePaste(e) {
      e.preventDefault(); // Detenemos el pegado nativo para tener control total
      
      // 1. Obtenemos el texto plano del portapapeles
      const text = (e.clipboardData || window.clipboardData).getData('text/plain');
      
      // 2. Extraemos solo los dígitos (máximo 8)
      const digits = text.replace(/\D/g, '').slice(0, 8);
      if (!digits) return;

      // 3. Insertamos los dígitos en la posición actual del cursor
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      
      const range = selection.getRangeAt(0);
      range.deleteContents(); // Borra lo seleccionado si hay algo
      
      const textNode = document.createTextNode(digits);
      range.insertNode(textNode);

      // 4. Movemos el cursor al final de lo insertado
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);

      // 5. IMPORTANTE: Disparamos manualmente el evento 'input'
      // Esto hace que se ejecute tu método onInput(e), el cual aplicará 
      // la máscara (MM/DD/YYYY) y validará los datos.
      const event = new Event('input', { bubbles: true });
      this.$refs.inputRef.dispatchEvent(event);
    },

    caretAtStart() {
      const sel = window.getSelection();
      return sel.anchorOffset === 0;
    },

    caretAtEnd() {
      const sel = window.getSelection();
      const el = this.$refs.inputRef;
      return sel.anchorOffset === el.innerText.length;
    },

    applyMask(digits, keepTrailingSlash) {
      if (!digits) return "";
      let r = "";
      
      // Bloque 1 (Día o Mes)
      r += digits.substring(0, 2);
      if (digits.length > 2) {
        r += "/" + digits.substring(2, 4);
      } else if (digits.length === 2 && keepTrailingSlash) {
        r += "/"; // Mantiene la barra si el usuario la escribió o borró hasta ahí
      }

      // Bloque 2 (Mes o Día)
      if (digits.length > 4) {
        r += "/" + digits.substring(4, 8);
      } else if (digits.length === 4 && keepTrailingSlash) {
        r += "/";
      }

      return r;
    },

    getCaretPosition(el) {
      const sel = window.getSelection();
      if (sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(el);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        return preCaretRange.toString().length;
      }
      return 0;
    },

    setCaretPosition(el, pos) {
      const sel = window.getSelection();
      const range = document.createRange();
      
      // Nos aseguramos de que el elemento tenga un nodo de texto limpio
      if (!el.firstChild) {
        el.appendChild(document.createTextNode(""));
      }
      
      let charCount = 0;
      let nodeStack = [el];
      let node;

      while ((node = nodeStack.pop())) {
        if (node.nodeType === 3) {
          const nextCount = charCount + node.length;
          if (pos <= nextCount) {
            range.setStart(node, pos - charCount);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            return;
          }
          charCount = nextCount;
        } else {
          let i = node.childNodes.length;
          while (i--) nodeStack.push(node.childNodes[i]);
        }
      }

      // Fallback al final del contenido si falla el loop
      const last = el.lastChild;
      range.setStart(last, last.nodeType === 3 ? last.length : 0);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    },

    emitChanged(value) {
      EventBus.$emit('onChanged', { Changed: true, componentName: this.name, value });
    },
    validateCell(val = null) {
      let state = true;
      const valueToCheck = val !== null ? val : String(this.displayValue ?? '').trim();
      const validatorRules = this.validatorRules ? [...this.validatorRules] : [];

      if (valueToCheck.length !== 0) {
        validatorRules.push(
          validationRules.length(10,
            this.$t('Msg.InvalidDate')
          ),
          validationRules.date(Utils.getShortDateFormat(),
              this.$t('Msg.InvalidDate')
          )
        );
      }

      if (validatorRules.length > 0) {
        const rules = {
          [this.name]: validatorRules
        };
        state = this.validateFields(rules, { [this.name]: valueToCheck });
      }

      this.invalid = !state;
      if (this.isFocused) {
        this.$emit('validationState', { name: this.name, invalid: this.invalid });
      }

      return state;
    },
  },

  mounted() {
    const el = this.$refs.inputRef;
    if (el) {
      el.addEventListener('paste', this.handlePaste);
    }
  },
  
  beforeDestroy() { // o beforeUnmount() en Vue 3
    const el = this.$refs.inputRef;
    if (el) {
      el.removeEventListener('paste', this.handlePaste);
    }
  }
};
</script>

<style scoped>
.editableCell { padding: 0 !important; min-width: 120px; }
.cellFocused { box-shadow: inset 0 0 0 1px #007bff !important; }
.cellWrapper { position: relative; display: flex; align-items: center; width: 100%; height: 100%; }
.cellDisplay { flex: 1; padding: 8px; cursor: pointer; text-align: center; }
.nonEditable { cursor: default; color: #555; }
.cellEditing { flex: 1; padding: 6px; outline: none; width: calc(100% - 6px); margin: 2px;
  border: 2px solid #007bff; border-radius: 4px; background-color: #fff; 
  cursor: text;}
.hasSearch .cellContent, .hasSearch .cellDisplay { padding-right: 30px; }
.cellContentInvalid { flex: 1; padding: 6px; outline: none; width: calc(100% - 6px); margin: 2px;
  border: 2px solid red; border-radius: 4px; background-color: #fff; }
.cellInvalid { background-color: #fae3de !important; box-shadow: inset 0 0 0 1px red !important; }
.cellFocusedInvalid { background-color: #fae3de !important; box-shadow: inset 0 0 0 1px #007bff !important; }
</style>
